import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Stack,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha,
  Tooltip,
  Pagination,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Search, Edit, Delete, Add, Save, Close, ExpandMore, ChevronRight } from '@mui/icons-material';
import { useEffect, useState, useCallback, useRef, useMemo, Fragment } from 'react';
import {
  listItemDescriptionMappings,
  setItemDescriptionMapping,
  deleteItemDescriptionMapping,
} from '@/api/receipt';
import { ItemDescriptionMapping } from '../../generated/receipt/v1/receipt_pb';
import { useDescriptionMappingStore } from '../store/descriptionMappingStore';

interface EditingMapping {
  originalDescription: string;
  customDescription: string;
  isNew: boolean;
}

export default function DescriptionManagerPage() {
  const theme = useTheme();
  const refreshDescriptionOptions = useDescriptionMappingStore((state) => state.refresh);
  const [mappings, setMappings] = useState<ItemDescriptionMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<ItemDescriptionMapping | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingMapping, setEditingMapping] = useState<EditingMapping | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ customDescription: string; originals: ItemDescriptionMapping[] } | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<{ customDescription: string; originals: ItemDescriptionMapping[] } | null>(null);
  const [editedGroupDescription, setEditedGroupDescription] = useState('');
  const [isSavingGroup, setIsSavingGroup] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<10 | 25 | 50>(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const MAX_INPUT_CHARS = 500;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getCharLimitHelper = (value: string) =>
    value.length >= MAX_INPUT_CHARS ? `${MAX_INPUT_CHARS}/${MAX_INPUT_CHARS} characters` : undefined;

  const getGroupKey = (customDescription: string) => customDescription || '__empty__';

  const groupedMappings = useMemo(() => {
    const groups = new Map<string, { customDescription: string; originals: ItemDescriptionMapping[] }>();

    mappings.forEach((mapping) => {
      const groupKey = getGroupKey(mapping.customDescription);
      const group = groups.get(groupKey) ?? {
        customDescription: mapping.customDescription,
        originals: [],
      };
      group.originals.push(mapping);
      groups.set(groupKey, group);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        originals: [...group.originals].sort((a, b) =>
          a.originalDescription.localeCompare(b.originalDescription)
        ),
      }))
      .sort((a, b) => a.customDescription.localeCompare(b.customDescription));
  }, [mappings]);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search change
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const fetchMappings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await listItemDescriptionMappings({
        page,
        pageSize,
        search: debouncedSearch || undefined,
      });
      setMappings(response.mappings);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load description mappings');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      groupedMappings.forEach((group) => {
        const key = getGroupKey(group.customDescription);
        if (next[key] === undefined) {
          next[key] = false;
        }
      });
      return next;
    });
  }, [groupedMappings]);

  const handleDeleteClick = (mapping: ItemDescriptionMapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!mappingToDelete) return;

    try {
      setIsDeleting(true);
      await deleteItemDescriptionMapping({
        originalDescription: mappingToDelete.originalDescription,
      });
      // Refetch to get updated pagination
      await fetchMappings();
      void refreshDescriptionOptions();
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete mapping');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMappingToDelete(null);
  };

  const handleEditClick = (mapping: ItemDescriptionMapping) => {
    setEditingMapping({
      originalDescription: mapping.originalDescription,
      customDescription: mapping.customDescription,
      isNew: false,
    });
    setEditDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingMapping({
      originalDescription: '',
      customDescription: '',
      isNew: true,
    });
    setEditDialogOpen(true);
  };

  const handleAddToGroup = (customDescription: string) => {
    setEditingMapping({
      originalDescription: '',
      customDescription,
      isNew: true,
    });
    setEditDialogOpen(true);
  };

  const handleDeleteGroupClick = (group: { customDescription: string; originals: ItemDescriptionMapping[] }) => {
    setGroupToDelete(group);
    setDeleteGroupDialogOpen(true);
  };

  const handleDeleteGroupConfirm = async () => {
    if (!groupToDelete) return;

    try {
      setIsDeletingGroup(true);
      // Delete all mappings in the group
      await Promise.all(
        groupToDelete.originals.map((mapping) =>
          deleteItemDescriptionMapping({
            originalDescription: mapping.originalDescription,
          })
        )
      );
      // Refetch to get updated pagination
      await fetchMappings();
      void refreshDescriptionOptions();
      setDeleteGroupDialogOpen(false);
      setGroupToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group');
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleDeleteGroupCancel = () => {
    setDeleteGroupDialogOpen(false);
    setGroupToDelete(null);
  };

  const handleEditGroupClick = (group: { customDescription: string; originals: ItemDescriptionMapping[] }) => {
    setGroupToEdit(group);
    setEditedGroupDescription(group.customDescription);
    setEditGroupDialogOpen(true);
  };

  const handleEditGroupCancel = () => {
    setEditGroupDialogOpen(false);
    setGroupToEdit(null);
    setEditedGroupDescription('');
  };

  const handleEditGroupSave = async () => {
    if (!groupToEdit || !editedGroupDescription.trim()) return;
    if (editedGroupDescription.trim() === groupToEdit.customDescription) {
      handleEditGroupCancel();
      return;
    }

    try {
      setIsSavingGroup(true);
      // Update all mappings in the group with the new custom description
      await Promise.all(
        groupToEdit.originals.map((mapping) =>
          setItemDescriptionMapping({
            originalDescription: mapping.originalDescription,
            customDescription: editedGroupDescription.trim(),
          })
        )
      );
      // Refetch to get updated data
      await fetchMappings();
      void refreshDescriptionOptions();
      setEditGroupDialogOpen(false);
      setGroupToEdit(null);
      setEditedGroupDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update group');
    } finally {
      setIsSavingGroup(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingMapping(null);
  };

  const handleEditSave = async () => {
    if (!editingMapping) return;
    if (!editingMapping.originalDescription.trim() || !editingMapping.customDescription.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      await setItemDescriptionMapping({
        originalDescription: editingMapping.originalDescription.trim(),
        customDescription: editingMapping.customDescription.trim(),
      });

      // Refetch to get updated data and pagination
      await fetchMappings();
      void refreshDescriptionOptions();
      setEditDialogOpen(false);
      setEditingMapping(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditDialogKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleEditCancel();
      return;
    }

    if (event.key === 'Enter') {
      if (
        isSaving ||
        !editingMapping?.originalDescription.trim() ||
        !editingMapping?.customDescription.trim()
      ) {
        return;
      }
      event.preventDefault();
      void handleEditSave();
    }
  };

  const handleEditGroupDialogKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleEditGroupCancel();
      return;
    }

    if (event.key === 'Enter') {
      if (isSavingGroup || !editedGroupDescription.trim()) {
        return;
      }
      event.preventDefault();
      void handleEditGroupSave();
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSize: 10 | 25 | 50 | null
  ) => {
    if (newSize !== null) {
      setPageSize(newSize);
      setPage(1); // Reset to first page when changing page size
    }
  };

  const handleGroupToggle = (groupKey: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Description Overrides
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Receipts use store descriptions by default - manage custom descriptions for receipt items here.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2, boxShadow: 'none' }}
          onClick={handleAddNew}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Search */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <TextField
            size="small"
            placeholder="Search descriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.slice(0, MAX_INPUT_CHARS))}
            helperText={getCharLimitHelper(searchQuery)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '40vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : totalCount === 0 && !error && !debouncedSearch ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No description mappings found. Add your first mapping to customize how receipt items are
          displayed.
        </Alert>
      ) : mappings.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No mappings match your search criteria.
        </Alert>
      ) : (
        <>
          {/* Desktop Table View */}
          <Card
            elevation={0}
            sx={{
              display: { xs: 'none', md: 'block' },
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedMappings.map((group) => {
                    const groupKey = getGroupKey(group.customDescription);
                    const isExpanded = expandedGroups[groupKey] ?? false;

                    return (
                      <Fragment key={groupKey}>
                        <TableRow
                          hover
                          onClick={() => handleGroupToggle(groupKey)}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            cursor: 'pointer',
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <IconButton
                                size="small"
                                onClick={() => handleGroupToggle(groupKey)}
                                sx={{ color: 'text.secondary' }}
                              >
                                {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                              </IconButton>
                              <Typography variant="body2" fontWeight="600">
                                {group.customDescription}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2, width: 180 }}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Edit description">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditGroupClick(group);
                                  }}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Add original description">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToGroup(group.customDescription);
                                  }}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                  }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete group">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteGroupClick(group);
                                  }}
                                  sx={{
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {group.originals.map((mapping) => (
                          <TableRow
                            key={mapping.originalDescription}
                            hover
                            sx={{
                              display: isExpanded ? 'table-row' : 'none',
                              '&:last-child td, &:last-child th': { border: 0 },
                              bgcolor: alpha(theme.palette.background.default, 0.5),
                            }}
                          >
                            <TableCell sx={{ py: 1.5, pl: 7 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
                              >
                                {mapping.originalDescription}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5 }}>
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditClick(mapping);
                                    }}
                                    sx={{
                                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                      },
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(mapping);
                                    }}
                                    sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.1),
                                      },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Mobile Card View */}
          <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {groupedMappings.map((group) => {
              const groupKey = getGroupKey(group.customDescription);
              const isExpanded = expandedGroups[groupKey] ?? false;
              return (
                <Card
                  key={groupKey}
                  elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleGroupToggle(groupKey)}
                          sx={{ color: 'text.secondary' }}
                        >
                          {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                        </IconButton>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Custom
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {group.customDescription}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.originals.length} original{group.originals.length === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit description">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditGroupClick(group)}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add original description">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleAddToGroup(group.customDescription)}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete group">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteGroupClick(group)}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                    {isExpanded && (
                      <Stack spacing={1}>
                        {group.originals.map((mapping) => (
                          <Card
                            key={mapping.originalDescription}
                            elevation={0}
                            sx={{
                              border: `1px dashed ${theme.palette.divider}`,
                              borderRadius: 2,
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Stack spacing={1}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                                >
                                  {mapping.originalDescription}
                                </Typography>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Edit />}
                                    onClick={() => handleEditClick(mapping)}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    Edit
                                  </Button>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(mapping)}
                                    sx={{
                                      border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
              );
            })}
          </Stack>

          {/* Pagination Controls */}
          <Card
            elevation={0}
            sx={{
              mt: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Per page:
                  </Typography>
                  <ToggleButtonGroup
                    value={pageSize}
                    exclusive
                    onChange={handlePageSizeChange}
                    size="small"
                  >
                    <ToggleButton value={10}>10</ToggleButton>
                    <ToggleButton value={25}>25</ToggleButton>
                    <ToggleButton value={50}>50</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {totalCount > 0
                      ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`
                      : '0 mappings'}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Mapping?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the mapping for
            <Box
              component="span"
              fontWeight="600"
              color="text.primary"
              sx={{ fontFamily: 'monospace' }}
            >
              {' '}
              {mappingToDelete?.originalDescription}
            </Box>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            autoFocus
            sx={{ borderRadius: 2, boxShadow: 'none' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Confirmation Dialog */}
      <Dialog
        open={deleteGroupDialogOpen}
        onClose={handleDeleteGroupCancel}
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Entire Group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the group
            <Box
              component="span"
              fontWeight="600"
              color="text.primary"
            >
              {' '}
              {groupToDelete?.customDescription}
            </Box>
            {' '}and all {groupToDelete?.originals.length} original description{groupToDelete?.originals.length === 1 ? '' : 's'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteGroupCancel}
            disabled={isDeletingGroup}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteGroupConfirm}
            color="error"
            variant="contained"
            disabled={isDeletingGroup}
            autoFocus
            sx={{ borderRadius: 2, boxShadow: 'none' }}
          >
            {isDeletingGroup ? 'Deleting...' : 'Delete Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog
        open={editGroupDialogOpen}
        onClose={handleEditGroupCancel}
        onKeyDown={handleEditGroupDialogKeyDown}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Description</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Rename the custom description for {groupToEdit?.originals.length} original item{groupToEdit?.originals.length === 1 ? '' : 's'}.
          </DialogContentText>
          <TextField
            label="Custom Description"
            value={editedGroupDescription}
            onChange={(e) => setEditedGroupDescription(e.target.value.slice(0, MAX_INPUT_CHARS))}
            fullWidth
            helperText={getCharLimitHelper(editedGroupDescription) || 'How you want these items to be displayed'}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleEditGroupCancel}
            disabled={isSavingGroup}
            variant="outlined"
            startIcon={<Close />}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditGroupSave}
            variant="contained"
            disabled={isSavingGroup || !editedGroupDescription.trim()}
            startIcon={<Save />}
            sx={{ borderRadius: 2, boxShadow: 'none' }}
          >
            {isSavingGroup ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        onKeyDown={handleEditDialogKeyDown}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingMapping?.isNew ? 'Add New Mapping' : 'Edit Mapping'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Original Description"
              value={editingMapping?.originalDescription || ''}
              onChange={(e) =>
                setEditingMapping((prev) =>
                  prev ? { ...prev, originalDescription: e.target.value.slice(0, MAX_INPUT_CHARS) } : null
                )
              }
              disabled={!editingMapping?.isNew}
              fullWidth
              helperText={
                editingMapping?.isNew
                  ? 'The exact text from the receipt (case-sensitive)'
                  : 'Cannot change original description'
              }
              InputProps={{
                sx: { fontFamily: 'monospace' },
              }}
            />
            <TextField
              label="Custom Description"
              value={editingMapping?.customDescription || ''}
              onChange={(e) =>
                setEditingMapping((prev) =>
                  prev ? { ...prev, customDescription: e.target.value.slice(0, MAX_INPUT_CHARS) } : null
                )
              }
              fullWidth
              helperText="How you want this item to be displayed"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleEditCancel}
            disabled={isSaving}
            variant="outlined"
            startIcon={<Close />}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={
              isSaving ||
              !editingMapping?.originalDescription.trim() ||
              !editingMapping?.customDescription.trim()
            }
            startIcon={<Save />}
            sx={{ borderRadius: 2, boxShadow: 'none' }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
